import * as _ from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { MouseEvent, VoidFunctionComponent } from 'react';

import { fetcherToPlainText } from '../../../../shared/fetchers';
import { renderRevision } from '../../../../shared/helpers/renderLocator';
import { usePackageLabelsRow } from '../../../hooks/api/packageLabels/usePackageLabelsRow';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { Badge } from '../../generic/Badge';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../generic/Button';
import Card from '../../generic/Card/Card';
import DetailsPanel, { DetailsPanelList } from '../../generic/DetailsPanel';
import Tooltip from '../../generic/Tooltip';
import { Section } from '../../shared/vulns/Drawer';
import type { Fetcher } from '../../../../shared/fetchers';
import type { VulnIssue } from '../../../../shared/issuesV2';
import type { DetailsPanelListRowProps } from '../../generic/DetailsPanel/DetailsPanelListRow';

import styles from './IssueDetails.module.less';

type DepthText =
  | `Direct (${number})`
  | `Transitive (${number})`
  | `Direct (${number}) and Transitive (${number})`;

/**
 * Determines the text for the depths section.
 *
 * @param {VulnIssue['depths']} depths - The various "depths" at which the dependency is used.
 */
const getDepthText = (
  depths: VulnIssue['depths'],
): DepthText => {
  if (depths.direct && depths.deep) {
    return `Direct (${depths.direct}) and Transitive (${depths.deep})`;
  }
  return depths.direct > depths.deep
    ? `Direct (${depths.direct})`
    : `Transitive (${depths.deep})`;
};

type Props = {
  additionalRows?: DetailsPanelListRowProps[];

  // Display the package correction message.
  isPackageCorrected?: boolean;

  // Callback to show snippet details (only for snippet issues with feature flag enabled)
  onShowSnippetDetails?: (snippetId: string) => void;
};

/**
 * IssueDetailsDependencySection displays the dependency info
 * for the package which flagged this vulnerability info.
 */
const IssueDetailsDependencySection: VoidFunctionComponent<Props> = ({
  additionalRows,
  isPackageCorrected,
  onShowSnippetDetails,
}) => {
  const issueDetails = useSelector(IssuesV2Store.selectors.getIssueDetails);
  const containerLayer = useSelector(IssuesV2Store.selectors.getContainerLayer);
  const packageLabelsRow = usePackageLabelsRow(issueDetails?.source.id, issueDetails?.projects);

  // Combine additional rows with package labels row
  const allAdditionalRows = useMemo(() => {
    const rows = [...(additionalRows || [])];
    if (packageLabelsRow) {
      rows.push(packageLabelsRow);
    }
    return rows;
  }, [additionalRows, packageLabelsRow]);

  if (!issueDetails) {
    return null;
  }

  const { source: { packageManager } } = issueDetails;
  const isSnippetIssue = !_.isNil(issueDetails.analysisSnippetId);

  // Handler for snippet details button
  const handleShowSnippetDetails = (event: MouseEvent) => {
    event.stopPropagation();
    if (onShowSnippetDetails && issueDetails.analysisSnippetId) {
      onShowSnippetDetails(issueDetails.analysisSnippetId);
    }
  };

  const rows: DetailsPanelListRowProps[] = _.compact([
    {
      label: 'Package name',
      content: (
        <span className="font-family-monospace">
          {issueDetails.source.name}
        </span>
      ),
    },
    issueDetails.source.version && {
      label: 'Version',
      content: (
        <Badge
          color="light-gray"
          shape="circle"
        >
          {renderRevision(issueDetails.source.id)}
        </Badge>
      ),
    },
    (issueDetails as VulnIssue).cpes?.[0] && {
      label: 'CPE',
      content: (
        <span className="font-family-monospace">
          {(issueDetails as VulnIssue).cpes[0]}
        </span>
      ),
    },
    {
      label: 'Depth(s)',
      content: (
        <span className="font-family-monospace">
          {getDepthText(issueDetails.depths)}
        </span>
      ),
    },
    containerLayer && {
      label: 'Container layer(s)',
      content: (
        <span className="font-family-monospace">
          {containerLayer}
        </span>
      ),
    },
    {
      label: 'Locator',
      content: (
        <span className="font-family-monospace">
          {issueDetails.source.id}
        </span>
      ),
    },
    {
      label: 'Package manager',
      content: (
        <span className="font-family-monospace">
          {fetcherToPlainText(packageManager as Fetcher) || packageManager}
        </span>
      ),
    },
    issueDetails.source.url && {
      label: 'Homepage',
      content: (
        <Tooltip tooltip={issueDetails.source.url} id="source-url">
          <a href={issueDetails.source.url} target="_blank" rel="noreferrer">
            <span className={styles.homePageLink}>
              {issueDetails.source.url}
            </span>
          </a>
        </Tooltip>
      ),
    },
    ...allAdditionalRows,

  ]);

  return (
    <Section>
      <DetailsPanel
        id="issue-details-dependency-section"
        target={isSnippetIssue ? 'Package' : 'Dependency'}
        loadingRowCount={rows.length}
        //  We only display this for snippets when callback is available
        targetButton={isSnippetIssue && onShowSnippetDetails ? (
          <Button
            onClick={handleShowSnippetDetails}
            type={BUTTON_TYPE.SECONDARY}
            size={BUTTON_SIZE.MEDIUM}
          >
            View Details
          </Button>
        ) : undefined}
      >
        {isSnippetIssue && (
          <div className={styles.snippetInfoBanner}>
            <Card variant="fill" color="blue" padding="xs" isFullWidth>
              <Card.Content>
                Identified as a snippet via fingerprint analysis.
              </Card.Content>
            </Card>
          </div>
        )}
        <DetailsPanelList rows={rows} />
        {isPackageCorrected && (
          <div className={styles.packageCorrectionMessage}>This package has been manually corrected</div>
        )}
      </DetailsPanel>
    </Section>
  );
};

export default IssueDetailsDependencySection;
